/**
 * SuperCivicaWorld — SVG overworld map for Super Civica World.
 * Visual language matches the world-intro cutscene: voxel island, backpack
 * kids, sunglasses sun, striped market, marble capitol. Not a Mario homage.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { EventBus, EVENTS } from '@/game/EventBus'
import { useGame } from '@/context/GameContext'
import type { Standard } from '@/api/client'
import overworldArt from '@/media/overworld-voxel.png'

// ─── Palette ────────────────────────────────────────────────────────────────
const M1 = {
  sky:        '#7cc8f8',
  skyHi:      '#aee0fb',
  sea:        '#1f74c8',
  seaShallow: '#52a8e8',
  beach:      '#f0d088',
  beachDark:  '#c69b48',
  geoGrass:   '#4ec24a',
  geoGrassDk: '#2b8430',
  geoHill:    '#3aa83a',
  geoMtn:     '#a8a4a0',
  geoMtnDk:   '#6e6a66',
  geoSnow:    '#ffffff',
  geoWater:   '#3aa0e8',
  seaBleed:   '#2e5a75', // letterbox ocean — sampled from the painted water
  civPlaza:   '#ddd6cc',
  civPlazaLt: '#f4efe6',
  civPlazaDk: '#c4bbb0',
  civAccent:  '#8c4eb8',
  civAccentDk:'#5a2f80',
  ecoFloor:   '#e69a4a',
  ecoFloorDk: '#a86420',
  ecoBrick:   '#d44a2a',
  ecoBrickDk: '#8a2210',
  ecoGold:    '#ffd840',
  hisSand:    '#e6c478',
  hisSandDk:  '#a87a32',
  hisAccent:  '#c08a2a',  // design-spec history region accent
  hisStone:   '#dec8a0',
  hisStoneDk: '#7e6244',
  hisShadow:  '#8a5a2a',
}

// ─── Utility ────────────────────────────────────────────────────────────────
function hexN(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`
}

// ─── Animation wrappers ───────────────────────────────────────────────────────
function Bob({ x = 0, y = 0, amp = 3, dur = 1.4, delay = 0, children }: {
  x?: number; y?: number; amp?: number; dur?: number; delay?: number; children: React.ReactNode
}) {
  return (
    <g transform={`translate(${x},${y})`}>
      <animateTransform attributeName="transform" type="translate"
        values={`0 0; 0 ${-amp}; 0 0`} dur={`${dur}s`} begin={`${delay}s`}
        repeatCount="indefinite" additive="sum"/>
      {children}
    </g>
  )
}

function Goat({ x = 0, y = 0, coat = '#f3eee4', shade = '#c9c0b0' }: {
  x?: number; y?: number; coat?: string; shade?: string
}) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <ellipse cx="0" cy="9" rx="7" ry="1.6" fill="#000" opacity=".22"/>
      <rect x="-6" y="-1" width="12" height="7" fill={coat}/>
      <rect x="-6" y="2" width="12" height="4" fill={shade} opacity=".45"/>
      <rect x="4" y="-5" width="6" height="6" fill={coat}/>
      <rect x="8" y="-7" width="2" height="3" fill={shade}/>
      <rect x="5" y="-4" width="1.5" height="1.5" fill="#1a0a00"/>
      <rect x="-6" y="6" width="2" height="4" fill={shade}/>
      <rect x="-2" y="6" width="2" height="4" fill={coat}/>
      <rect x="2" y="6" width="2" height="4" fill={shade}/>
      <rect x="5" y="6" width="2" height="4" fill={coat}/>
      <rect x="-7" y="1" width="2" height="2" fill={shade}/>
    </g>
  )
}

// ─── Coin ────────────────────────────────────────────────────────────────────
function Coin({ x, y, r = 6, delay = 0 }: { x: number; y: number; r?: number; delay?: number }) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <g>
        <animateTransform attributeName="transform" type="scale"
          values="1 1; -1 1; 1 1" keyTimes="0;.5;1" dur="1.2s" begin={`${delay}s`}
          repeatCount="indefinite" additive="sum"/>
        <circle r={r} fill="#f4c63d"/>
        <circle r={r - 1.5} fill="#ffd860"/>
        <circle r={r - 3} fill="#a8742a" opacity=".35"/>
      </g>
      <g>
        <animate attributeName="opacity" values="0;1;0;0" dur="2s" begin={`${delay + 0.4}s`} repeatCount="indefinite"/>
        <polygon points={`0,${-r - 4} 1,${-r - 2} 3,${-r - 2} 1.5,${-r - 1} 2,${-r + 1} 0,${-r} -2,${-r + 1} -1.5,${-r - 1} -3,${-r - 2} -1,${-r - 2}`} fill="#fff"/>
      </g>
    </g>
  )
}

// ─── Node-state design tokens ────────────────────────────────────────────────
const STATE_CLEARED = '#7adb7a'   // gold-green: cleared node fill
const STATE_LOCKED  = '#9a958c'   // grey: locked node fill

// ─── Padlock badge (locked nodes) ────────────────────────────────────────────
function Padlock({ x = 0, y = 0 }: { x?: number; y?: number }) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <circle r="7" fill="#1a1a1a" opacity=".9"/>
      <rect x="-3.5" y="-1" width="7" height="6" rx="1" fill="#ffd840"/>
      <path d="M -2 -1 v -1.5 a 2 2 0 0 1 4 0 v 1.5" fill="none" stroke="#ffd840" strokeWidth="1.2"/>
      <rect x="-0.6" y="1" width="1.2" height="2" fill="#7a5a10"/>
    </g>
  )
}

// ─── Pulse ring (next-node indicator) ────────────────────────────────────────
function PulseRing({ r = 22, color = '#fff' }: { r?: number; color?: string }) {
  return (
    <circle r={r} fill="none" stroke={color} strokeWidth="2" opacity="0">
      <animate attributeName="r" values={`${r};${r + 10}`} dur="1.4s" repeatCount="indefinite"/>
      <animate attributeName="opacity" values=".9;0" dur="1.4s" repeatCount="indefinite"/>
    </circle>
  )
}

// ─── Bouncing gold chevron (points player to the next node) ──────────────────
function NextChevron() {
  return (
    <g transform="translate(0,-28)">
      <g>
        <animateTransform attributeName="transform" type="translate"
          values="0 0; 0 4; 0 0" dur="0.9s" repeatCount="indefinite" additive="sum"/>
        <polygon points="-6,-4 6,-4 0,4" fill="#ffd840" stroke="#a8742a" strokeWidth="1"/>
      </g>
    </g>
  )
}

function LandmarkGlyph({ biome, color }: { biome: string; color: string }) {
  if (biome === 'geography') {
    return (
      <g shapeRendering="crispEdges">
        <rect x="-1.5" y="2" width="3" height="6" fill="#5a3a1a"/>
        <rect x="-6" y="-2" width="12" height="5" fill={color}/>
        <rect x="-4" y="-7" width="8" height="5" fill={M1.geoGrass}/>
      </g>
    )
  }
  if (biome === 'civics') {
    return (
      <g shapeRendering="crispEdges">
        <rect x="-3" y="-8" width="6" height="2" fill="#f0ecea"/>
        <rect x="-2" y="-6" width="4" height="12" fill="#ede8f8"/>
        <rect x="-3" y="6" width="6" height="2" fill={color}/>
      </g>
    )
  }
  if (biome === 'economics') {
    return (
      <g shapeRendering="crispEdges">
        <rect x="-7" y="2" width="14" height="6" fill="#a07a4a"/>
        <rect x="-7" y="-8" width="4" height="10" fill={color}/>
        <rect x="-3" y="-8" width="4" height="10" fill="#ffd840"/>
        <rect x="1" y="-8" width="4" height="10" fill={color}/>
        <rect x="5" y="-8" width="2" height="10" fill="#ffd840"/>
      </g>
    )
  }
  return (
    <g shapeRendering="crispEdges">
      <polygon points="-8,8 8,8 0,-8" fill={M1.hisStone}/>
      <polygon points="0,8 8,8 0,-8" fill={M1.hisStoneDk}/>
      <ellipse cx="0" cy="-4" rx="2" ry="1.5" fill="#ffd840"/>
    </g>
  )
}

// ─── State-aware landmark node (locked / available / next / cleared) ─────────
function StateBlock({ x, y, state, color, name, biome = 'geography' }: {
  x: number; y: number
  state: 'locked' | 'available' | 'next' | 'cleared'
  color: string; name?: string; biome?: string
}) {
  const isLocked  = state === 'locked'
  const isCleared = state === 'cleared'
  const isNext    = state === 'next'
  const fill      = isLocked ? STATE_LOCKED : isCleared ? STATE_CLEARED : color
  const doBob     = !isLocked   // locked nodes are still

  const blockBody = (
    <g shapeRendering="crispEdges">
      <ellipse cx="0" cy="14" rx="13" ry="2.5" fill="#000" opacity={isLocked ? '.15' : '.22'}/>
      <rect x="-12" y="-12" width="24" height="24" fill={fill}/>
      <rect x="-12" y="-12" width="24" height="3" fill="#fff" opacity=".5"/>
      <rect x="-12" y="9" width="24" height="3" fill="#000" opacity=".25"/>
      <rect x="9" y="-12" width="3" height="24" fill="#000" opacity=".25"/>
      <rect x="-12" y="-12" width="3" height="24" fill="#fff" opacity=".2"/>
      {isCleared
        ? <text y="7" textAnchor="middle"
            style={{ font: 'bold 17px "Press Start 2P", monospace', fill: '#fff',
                     filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.4))' }}>★</text>
        : <g transform="translate(0,1)"><LandmarkGlyph biome={biome} color={fill}/></g>}
    </g>
  )

  const truncName = name && name.length > 24 ? name.slice(0, 23) + '…' : (name ?? '')
  const pillSize  = mts(6.5)
  const pillRef   = useRef<SVGTextElement>(null)
  const pillTextW = useMeasuredTextWidth(pillRef, truncName.length * pillSize, [truncName, pillSize])
  const pillW     = Math.max(60, Math.ceil(pillTextW) + 16)
  const pillH     = pillSize + PLATE_PAD_Y * 2 + 2

  return (
    <g transform={`translate(${x},${y})`} opacity={isLocked ? .72 : 1}>
      {/* Cleared glow halo */}
      {isCleared && <circle r="20" fill={STATE_CLEARED} opacity=".25"/>}
      {/* Next-node pulse ring */}
      {isNext && <PulseRing r={20} color="#fff"/>}
      {/* Bob wrapper — locked nodes get no animation */}
      {doBob ? (
        <g>
          <animateTransform attributeName="transform" type="translate"
            values="0 0; 0 -2; 0 0" dur="2s" repeatCount="indefinite" additive="sum"/>
          {blockBody}
        </g>
      ) : blockBody}
      {/* White selection outline for available + next */}
      {(state === 'available' || isNext) && (
        <rect x="-14" y="-14" width="28" height="28" fill="none"
          stroke="#fff" strokeWidth="1.5" opacity={isNext ? .9 : .4}/>
      )}
      {/* Padlock badge bottom-right (locked only) */}
      {isLocked && <Padlock x={11} y={11}/>}
      {/* Bouncing chevron above (next only) */}
      {isNext && <NextChevron/>}
      {/* Name pill below (next only) */}
      {isNext && truncName && (
        <g transform="translate(0,32)">
          <rect x={-pillW / 2} y="-1" width={pillW} height={pillH} rx="7"
            fill="#11131f" stroke="#ffd840" strokeWidth="1"/>
          <text ref={pillRef} y={pillH / 2 - 0.5} textAnchor="middle" dominantBaseline="central"
            style={{ font: `${pillSize}px "Press Start 2P", monospace`, fill: '#ffe28a' }}>
            {truncName}
          </text>
        </g>
      )}
    </g>
  )
}

// ─── Map-wide text scale ─────────────────────────────────────────────────────
// Set once per render from the accessibility text-size setting (1 / 1.35 / 1.7)
// so every SVG label in this file scales without threading a prop through
// each decorative component. All label components live in this module.
let TS = 1
const mts = (n: number) => Math.round(n * TS * 10) / 10

// ─── Measured text width ─────────────────────────────────────────────────────
// Plates must fit the text ACTUALLY rendered, which varies with (a) the a11y
// text-scale, (b) the a11y readable-font override (proportional, applied via
// CSS !important — so any "monospace advance × length" estimate is wrong the
// moment it's on), and (c) webfont load timing. So: render the text, measure
// its painted bounds, size the plate from that. Estimate is only the
// first-paint fallback; re-measures on font toggle and when fonts finish
// loading. All label plates in this file MUST go through this — do not size
// plates by character count.
function useMeasuredTextWidth(
  ref: React.RefObject<SVGTextElement | null>,
  estimate: number,
  deps: readonly unknown[],
) {
  const { readableFont } = useGame()   // re-measure when the font override toggles
  const [w, setW] = useState(estimate)
  useLayoutEffect(() => {
    let cancelled = false
    const measure = () => {
      if (cancelled) return
      const el = ref.current
      if (!el) return
      try {
        const bb = el.getBBox()        // painted bounds — includes letter-spacing
        if (bb.width > 0) setW(bb.width)
      } catch { /* jsdom / detached node — keep estimate */ }
    }
    measure()
    // Webfonts can finish loading after first paint; re-measure then.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {})
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readableFont, estimate, ...deps])
  return w
}

// ─── Map label with a solid dark plate behind it ────────────────────────────
// THE standard map label: one plate style (dark, rounded, hairline border),
// one padding, text vertically centered via dominantBaseline so it stays
// centered in any font. `y` is the label's visual center-ish anchor kept
// compatible with the old baseline-based call sites.
const PLATE_PAD_X = 5
const PLATE_PAD_Y = 3
function MapLabel({ y, text, fill, fontSize = 7, letterSpacing = 0 }: {
  y: number; text: string; fill: string; fontSize?: number; letterSpacing?: number
}) {
  const size = mts(fontSize)
  const ref  = useRef<SVGTextElement>(null)
  const textW = useMeasuredTextWidth(ref, text.length * (size + letterSpacing), [text, size, letterSpacing])
  const w  = Math.ceil(textW) + PLATE_PAD_X * 2
  const h  = size + PLATE_PAD_Y * 2
  const cy = y - size * 0.38   // old call sites passed a text baseline; keep anchors stable
  return (
    <g>
      <rect x={-w / 2} y={cy - h / 2} width={w} height={h} rx="3"
        fill="rgba(8,8,22,0.85)" stroke="rgba(0,0,0,0.55)" strokeWidth="0.75"/>
      <text ref={ref} y={cy} textAnchor="middle" dominantBaseline="central"
        style={{ font: `${size}px "Press Start 2P", monospace`, fill,
                 letterSpacing: `${letterSpacing}px` }}>{text}</text>
    </g>
  )
}

function WavingFlag({ x, y, color, locked }: { x: number; y: number; color: string; locked: boolean }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <rect x="-.5" y="0" width="1" height="18" fill="#3a2613"/>
      <g>
        {!locked && (
          <animateTransform attributeName="transform" type="scale"
            values="1 1; .6 1; 1 1; -.6 1; 1 1" keyTimes="0;.25;.5;.75;1"
            dur="1.6s" repeatCount="indefinite" additive="sum"/>
        )}
        <polygon points="0,0 14,2 14,8 0,10" fill={color}/>
        <polygon points="0,0 14,2 0,4" fill="#fff" opacity=".25"/>
      </g>
    </g>
  )
}

// ─── Biome landmark boss (peak / hall / pyramid / capitol wing) ──────────────
function LandmarkBoss({ x, y, biome, final = false, flagColor = '#e02828', name, state = 'available',
  labelDx = 0, labelDy = 38 }: {
  x: number; y: number; biome: string; final?: boolean
  flagColor?: string; name?: string
  state?: 'locked' | 'available' | 'next' | 'cleared'
  labelDx?: number; labelDy?: number
}) {
  const isLocked  = state === 'locked'
  const isCleared = state === 'cleared'
  const accent    = isLocked ? '#7a7466' : isCleared ? '#ffd840' : flagColor

  let body: ReactNode
  if (biome === 'geography') {
    body = (
      <g>
        <polygon points="-36,22 36,22 0,-34" fill={isLocked ? '#8a8880' : M1.geoMtn}/>
        <polygon points="0,22 36,22 0,-34" fill={isLocked ? '#5a5850' : M1.geoMtnDk}/>
        <polygon points="-10,-16 10,-16 0,-34" fill={M1.geoSnow}/>
        <rect x="-4" y="8" width="8" height="14" fill="#2a2014"/>
        {final && (
          <g opacity=".75">
            <ellipse cx="0" cy="-8" rx="10" ry="16" fill="#6ec9ff" opacity=".35">
              <animate attributeName="opacity" values=".2;.5;.2" dur="2.4s" repeatCount="indefinite"/>
            </ellipse>
          </g>
        )}
        <WavingFlag x={0} y={-48} color={accent} locked={isLocked}/>
      </g>
    )
  } else if (biome === 'economics') {
    body = (
      <g>
        <rect x="-28" y="-4" width="56" height="26" fill={isLocked ? '#8a8880' : M1.ecoBrick}/>
        <rect x="-28" y="-4" width="56" height="4" fill={isLocked ? '#5a5850' : M1.ecoBrickDk}/>
        {[-24,-16,-8,0,8,16,24].map((cx, i) => (
          <rect key={i} x={cx - 2} y="-16" width="4" height="12" fill={i % 2 ? '#ffd840' : accent}/>
        ))}
        <rect x="-6" y="8" width="12" height="14" fill="#2a2014"/>
        {final && <rect x="16" y="-28" width="8" height="24" fill="#fff"/>}
        {final && <rect x="16" y="-32" width="8" height="4" fill="#ffd840"/>}
        <WavingFlag x={0} y={-28} color={accent} locked={isLocked}/>
      </g>
    )
  } else if (biome === 'history') {
    const w = final ? 52 : 42
    body = (
      <g>
        <polygon points={`${-w},22 ${w},22 0,-36`} fill={isLocked ? '#8a8880' : M1.hisStone}/>
        <polygon points={`0,22 ${w},22 0,-36`} fill={isLocked ? '#5a5850' : M1.hisStoneDk}/>
        {[0.25, 0.5, 0.75].map((t, i) => (
          <line key={i} x1={-w * (1 - t)} y1={22 - 58 * t} x2={w * (1 - t)} y2={22 - 58 * t}
            stroke={M1.hisShadow} strokeWidth="1" opacity=".5"/>
        ))}
        <ellipse cx="0" cy="-28" rx="5" ry="3.5" fill="#ffd840"/>
        <rect x="-5" y="8" width="10" height="14" fill="#2a2014"/>
        <WavingFlag x={0} y={-48} color={accent} locked={isLocked}/>
      </g>
    )
  } else {
    body = (
      <g>
        <rect x="-32" y="4" width="64" height="18" fill={isLocked ? '#a8a4a0' : '#f0ecea'}/>
        {[-20,-10,0,10,20].map((cx, i) => (
          <rect key={i} x={cx - 3} y="-8" width="6" height="26" fill="#ede8f8"/>
        ))}
        <polygon points="-28,-8 28,-8 0,-26" fill={isLocked ? '#909090' : '#e8c84a'}/>
        <path d="M -10 -20 Q 0 -36 10 -20" fill={isLocked ? '#686868' : '#3a6fb0'}/>
        <rect x="-5" y="8" width="10" height="14" fill="#2a2014"/>
        <WavingFlag x={0} y={-40} color={accent} locked={isLocked}/>
      </g>
    )
  }

  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges" opacity={isLocked ? .78 : 1}>
      <g transform="scale(0.7)">
        {!isLocked && (
          <circle r="44" fill={isCleared ? '#ffd840' : '#ffd55a'} opacity={isCleared ? '.35' : '.18'}>
            <animate attributeName="r" values="40;48;40" dur="3s" repeatCount="indefinite"/>
            <animate attributeName="opacity"
              values={isCleared ? '.35;.55;.35' : '.18;.35;.18'} dur="3s" repeatCount="indefinite"/>
          </circle>
        )}
        <ellipse cx="0" cy="22" rx="32" ry="5" fill="#000" opacity=".28"/>
        {body}
        {isLocked && <Padlock x={0} y={8}/>}
      </g>
      {name && (
        <g transform={`translate(${labelDx},${labelDy})`}>
          <MapLabel y={0} text={name.toUpperCase()} fill="#ffe28a" letterSpacing={0.5}/>
        </g>
      )}
    </g>
  )
}

// ─── Realm gate (footbridge / stone arch between biomes) ─────────────────────
function RealmGate({ x, y, color = '#3fa83f', dark = '#1e6b1e', label }: {
  x: number; y: number; color?: string; dark?: string; label?: string
}) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <ellipse cx="0" cy="18" rx="18" ry="3" fill="#000" opacity=".22"/>
      <rect x="-16" y="-10" width="6" height="26" fill="#c8c2b4"/>
      <rect x="10" y="-10" width="6" height="26" fill="#c8c2b4"/>
      <rect x="-16" y="-10" width="2" height="26" fill="#fff" opacity=".25"/>
      <rect x="14" y="-10" width="2" height="26" fill="#8a8478" opacity=".5"/>
      <rect x="-18" y="-16" width="36" height="8" fill={color}/>
      <rect x="-18" y="-16" width="36" height="2" fill="#fff" opacity=".25"/>
      <rect x="-18" y="-10" width="36" height="2" fill={dark}/>
      <rect x="-10" y="6" width="20" height="4" fill="#8a6232"/>
      {label && <MapLabel y={32} text={label} fill="#fff" fontSize={6.5}/>}
    </g>
  )
}

// ─── Hill ────────────────────────────────────────────────────────────────────
function Hill({ x, y, w = 80, color = M1.geoHill, dark = M1.geoGrassDk, face = true }: {
  x: number; y: number; w?: number; color?: string; dark?: string; face?: boolean
}) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <path d={`M ${-w / 2} 0 Q 0 ${-w * 0.7}, ${w / 2} 0 Z`} fill={color}/>
      <path d={`M ${-w / 2 + 6} 0 Q 0 ${-w * 0.55}, ${w / 2 - 6} 0 Z`} fill={dark} opacity=".4"/>
      {face && (
        <g>
          <circle cx={-w * 0.18} cy={-w * 0.28} r={w * 0.06} fill="#fff"/>
          <circle cx={w * 0.18} cy={-w * 0.28} r={w * 0.06} fill="#fff"/>
          <circle cx={-w * 0.18} cy={-w * 0.27} r={w * 0.03} fill="#3a2613"/>
          <circle cx={w * 0.18} cy={-w * 0.27} r={w * 0.03} fill="#3a2613"/>
          <path d={`M ${-w * 0.12} ${-w * 0.18} q ${w * 0.12} ${w * 0.07}, ${w * 0.24} 0`}
            fill="none" stroke="#3a2613" strokeWidth="1.5"/>
          <ellipse cx={-w * 0.3} cy={-w * 0.2} rx={w * 0.05} ry={w * 0.025} fill="#ff8a8a" opacity=".6"/>
          <ellipse cx={w * 0.3} cy={-w * 0.2} rx={w * 0.05} ry={w * 0.025} fill="#ff8a8a" opacity=".6"/>
        </g>
      )}
    </g>
  )
}

// ─── Voxel cloud (cutscene: cube puffs with faces) ───────────────────────────
function VoxelCloud({ x, y, scale = 1, drift = 0 }: {
  x: number; y: number; scale?: number; drift?: number
}) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`} shapeRendering="crispEdges">
      {drift !== 0 && (
        <animateTransform attributeName="transform" type="translate"
          values={`0 0; ${drift} 0; 0 0`} dur="14s" repeatCount="indefinite" additive="sum"/>
      )}
      <rect x="-22" y="-2" width="14" height="12" fill="#fff"/>
      <rect x="-8" y="-10" width="18" height="18" fill="#fff"/>
      <rect x="8" y="-2" width="14" height="12" fill="#fff"/>
      <rect x="-16" y="6" width="30" height="6" fill="#e8f0f8"/>
      <rect x="-5" y="-4" width="2" height="2" fill="#3a2613"/>
      <rect x="4" y="-4" width="2" height="2" fill="#3a2613"/>
      <rect x="-3" y="1" width="7" height="1.5" fill="#3a2613"/>
      <ellipse cx="-8" cy="0" rx="2.2" ry="1.2" fill="#ff8aa8" opacity=".7"/>
      <ellipse cx="9" cy="0" rx="2.2" ry="1.2" fill="#ff8aa8" opacity=".7"/>
    </g>
  )
}

// ─── Pacing goat / market-goer ───────────────────────────────────────────────
function PacingCritter({ x, y, range = 30, dur = 4, kind = 'goat' }: {
  x: number; y: number; range?: number; dur?: number; kind?: string
}) {
  const palettes: Record<string, { coat: string; shade: string }> = {
    goat:   { coat: '#f3eee4', shade: '#c9c0b0' },
    sandy:  { coat: '#e6c478', shade: '#a87a32' },
    purple: { coat: '#c89ee6', shade: '#5a2f80' },
  }
  const p = palettes[kind] ?? palettes.goat
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <g>
        <animateTransform attributeName="transform" type="translate"
          values={`0 0; ${range} 0; ${range} 0; 0 0; 0 0`} keyTimes="0;.45;.5;.95;1"
          dur={`${dur}s`} repeatCount="indefinite" additive="sum"/>
        <Goat x={0} y={0} coat={p.coat} shade={p.shade}/>
      </g>
    </g>
  )
}

// ─── Backpack kid — matches avatar picker + world-intro cutscene ─────────────
// hair → hat, outfit → body, skin → face/hands.
function BackpackKid({ x, y, hat = '#3a2613', overalls = '#1a4eb0', skin = '#f6c89a',
  label, scale = 1, map = true }: {
  x: number; y: number; hat?: string; overalls?: string; skin?: string
  label?: string; scale?: number; map?: boolean
}) {
  const pack = '#5a3a1a'
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`} shapeRendering="crispEdges">
      <ellipse cx="0" cy="17" rx="8" ry="2" fill="#000" opacity=".25"/>
      <g>
        <animateTransform attributeName="transform" type="translate"
          values="0 0; 0 -2; 0 0" dur="1s" repeatCount="indefinite" additive="sum"/>
        <rect x="-7" y="-15" width="14" height="5" fill={hat}/>
        <rect x="-8" y="-13" width="2"  height="3" fill={hat}/>
        <rect x="6"  y="-13" width="2"  height="3" fill={hat}/>
        <rect x="-6" y="-10" width="12" height="9" fill={skin}/>
        <rect x="-4" y="-8"  width="2" height="2" fill="#1a0a00"/>
        <rect x="2"  y="-8"  width="2" height="2" fill="#1a0a00"/>
        <rect x="-2" y="-2"  width="4" height="1" fill="#1a0a00" opacity=".55"/>
        <rect x="-7" y="-1" width="14" height="14" fill={overalls}/>
        <rect x="-7" y="-1" width="14" height="2"  fill={skin} opacity=".45"/>
        {/* Backpack */}
        <rect x="5" y="1" width="6" height="9" fill={pack}/>
        <rect x="5" y="1" width="6" height="2" fill="#c08a2a"/>
        <rect x="4" y="3" width="1" height="5" fill="#3a2613"/>
        {/* Arms */}
        <rect x="-10" y="1" width="3" height="6" fill={skin}/>
        {map && (
          <g>
            <rect x="-14" y="0" width="6" height="5" fill="#efe4c4" stroke="#7a6232" strokeWidth=".6"/>
            <path d="M -13 1.5 L -9 2.5 L -11 4" fill="none" stroke="#3aa83a" strokeWidth=".6"/>
          </g>
        )}
        <rect x="7" y="1" width="3" height="6" fill={skin}/>
        <rect x="-6" y="13" width="4" height="3" fill={overalls}/>
        <rect x="2"  y="13" width="4" height="3" fill={overalls}/>
        <rect x="-7" y="16" width="5" height="2" fill="#1a0a00"/>
        <rect x="2"  y="16" width="5" height="2" fill="#1a0a00"/>
      </g>
      {label && (
        <text y="-19" textAnchor="middle"
          style={{ font: `${mts(6.5)}px "Press Start 2P", monospace`, fill: '#fff',
                   filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.6))' }}>{label}</text>
      )}
    </g>
  )
}

// ─── Star ────────────────────────────────────────────────────────────────────
function MapStar({ x, y, r = 8, color = '#ffd840', delay = 0 }: {
  x: number; y: number; r?: number; color?: string; delay?: number
}) {
  const pts: string[] = []
  for (let i = 0; i < 10; i++) {
    const a = (i * Math.PI) / 5 - Math.PI / 2
    const rr = i % 2 === 0 ? r : r * 0.45
    pts.push(`${Math.cos(a) * rr},${Math.sin(a) * rr}`)
  }
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values="0;360" dur="3.5s" begin={`${delay}s`} repeatCount="indefinite" additive="sum"/>
        <polygon points={pts.join(' ')} fill={color}/>
        <polygon points={pts.join(' ')} fill="none" stroke="#a8742a" strokeWidth=".8"/>
        <circle cx={-r * 0.25} cy={-r * 0.1} r={r * 0.18} fill="#3a2613"/>
        <circle cx={r * 0.25} cy={-r * 0.1} r={r * 0.18} fill="#3a2613"/>
        <path d={`M ${-r * 0.2} ${r * 0.18} q ${r * 0.2} ${r * 0.15}, ${r * 0.4} 0`}
          fill="none" stroke="#3a2613" strokeWidth="1"/>
      </g>
    </g>
  )
}

// ─── Dotted path ──────────────────────────────────────────────────────────────
// `done` = both endpoint nodes are proficient-or-mastered → the route "lights up"
// as a solid gold line (Mario-overworld "the world remembers" convention).
function DottedPath({ from, to, via, color = '#fff', dur = 1.6, locked = false, done = false }: {
  from: [number, number]; to: [number, number]; via?: [number, number][]
  color?: string; dur?: number; locked?: boolean; done?: boolean
}) {
  const pts = [from, ...(via ?? []), to]
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ')
  if (done) {
    return (
      <g>
        <path d={d} fill="none" stroke={M1.ecoGold} strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.95"/>
        <path d={d} fill="none" stroke="#fff6c8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
      </g>
    )
  }
  return (
    <path d={d} fill="none"
      stroke={color} strokeWidth="2.6"
      strokeDasharray={locked ? '3 6' : '2 6'} strokeLinecap="round" strokeLinejoin="round"
      opacity={locked ? 0.3 : 0.82}>
      {!locked && (
        <animate attributeName="stroke-dashoffset" values="0;-32" dur={`${dur}s`} repeatCount="indefinite"/>
      )}
    </path>
  )
}

// ─── Crown (cleared-realm marker) ─────────────────────────────────────────────
function Crown({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`} shapeRendering="crispEdges">
      <polygon points="-7,4 7,4 6,-3 3,1 0,-6 -3,1 -6,-3" fill={M1.ecoGold} stroke="#a8742a" strokeWidth=".6"/>
      <rect x="-7" y="3.5" width="14" height="2.5" fill={M1.ecoGold} stroke="#a8742a" strokeWidth=".4"/>
      <circle cx="-6" cy="-3" r="1.1" fill="#fff6c8"/>
      <circle cx="0" cy="-6" r="1.1" fill="#fff6c8"/>
      <circle cx="6" cy="-3" r="1.1" fill="#fff6c8"/>
    </g>
  )
}

// ─── Realm banner with progress meter ─────────────────────────────────────────
// Shows a glanceable target ("14 / 21") per realm — flow + goal-gradient. When the
// realm is cleared the banner turns gold + crowned and the meter reads "CLEARED ✓".
// One title size for every realm; the bar auto-fits the measured title (any
// font, any a11y scale) and its height tracks the text so nothing overflows.
function RealmBanner({ x, y, w, title, dark, light, progress, ts }: {
  x: number; y: number; w: number; title: string; dark: string; light: string
  progress?: { total: number; done: number; mastered: number; complete: boolean }
  ts: number
}) {
  const total    = progress?.total ?? 0
  const done     = progress?.done ?? 0
  const complete = !!progress?.complete
  const started  = done > 0
  const ratio    = total > 0 ? done / total : 0

  const titleSize = Math.round(9 * ts)
  const metaSize  = Math.round(7 * ts)
  const titleRef  = useRef<SVGTextElement>(null)
  const labelRef  = useRef<SVGTextElement>(null)
  const label     = complete ? 'CLEARED ✓' : started ? `${done} / ${total}` : 'NOT STARTED'
  const titleW    = useMeasuredTextWidth(titleRef, title.length * titleSize, [title, titleSize])
  const labelW    = useMeasuredTextWidth(labelRef, label.length * metaSize, [label, metaSize])

  // Bar fits title (+crown when cleared) and never renders narrower than the
  // meter label needs; `w` from the call site is only a minimum for map layout.
  const crownW = complete ? 24 : 0
  const bw     = Math.max(w, Math.ceil(titleW) + crownW + 24, Math.ceil(labelW) + 32)
  const half   = bw / 2
  const bh     = titleSize + 11          // title bar height tracks the text
  const barY   = 6 - bh                  // bar bottom stays anchored at y=6
  const pad    = 8
  const innerW = bw - pad * 2
  const meterY = 9
  const meterH = metaSize + 6            // meter height tracks its label
  const barBg  = complete ? '#8a6d10' : dark
  const barTop = complete ? M1.ecoGold : light
  return (
    <g transform={`translate(${x},${y})`}>
      {/* Title bar */}
      <rect x={-half} y={barY} width={bw} height={bh} fill={barBg} rx="3"/>
      <rect x={-half} y={barY} width={bw} height={4} fill={barTop}/>
      {complete && <Crown x={-half + 13} y={barY + bh / 2 + 4} scale={0.85}/>}
      <text ref={titleRef} textAnchor="middle" dominantBaseline="central"
        x={complete ? crownW / 2 : 0} y={barY + bh / 2 + 2}
        style={{ fontSize: `${titleSize}px`, fill: complete ? M1.ecoGold : '#fff',
                 letterSpacing: '1px', filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.5))' }}>
        {title}
      </text>
      {/* Progress meter */}
      <rect x={-half + pad} y={meterY} width={innerW} height={meterH} rx={meterH / 2}
        fill="#000" opacity="0.35"/>
      {started && (
        <rect x={-half + pad} y={meterY} width={Math.max(meterH, innerW * ratio)} height={meterH}
          rx={meterH / 2} fill={complete ? M1.ecoGold : light}/>
      )}
      <text ref={labelRef} textAnchor="middle" dominantBaseline="central" y={meterY + meterH / 2 + 1}
        style={{ fontSize: `${metaSize}px`,
                 fill: complete ? '#3a2a00' : '#fff',
                 letterSpacing: '.5px', filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.6))' }}>
        {label}
      </text>
    </g>
  )
}

// ─── "FINAL BOSS" capstone tag ────────────────────────────────────────────────
function FinalBossTag({ x, y, ts, cleared }: { x: number; y: number; ts: number; cleared: boolean }) {
  const size = Math.round(6 * ts)
  const ref  = useRef<SVGTextElement>(null)
  const textW = useMeasuredTextWidth(ref, 13 * size, [size])
  const w = Math.ceil(textW) + PLATE_PAD_X * 2 + 4
  const h = size + PLATE_PAD_Y * 2 + 2
  return (
    <g transform={`translate(${x},${y})`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx="3"
        fill={cleared ? '#8a6d10' : '#1a1a2e'} stroke={cleared ? M1.ecoGold : '#ffd860'} strokeWidth="1"/>
      <text ref={ref} textAnchor="middle" dominantBaseline="central" y={0.5}
        style={{ fontSize: `${size}px`, fill: cleared ? M1.ecoGold : '#ffd860', letterSpacing: '.5px' }}>
        ★ FINAL BOSS
      </text>
    </g>
  )
}

// ─── Tree (pine or bush) ──────────────────────────────────────────────────────
function TreeM({ x, y, type = 'pine' }: { x: number; y: number; type?: string }) {
  if (type === 'pine') return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <rect x="-2" y="6" width="4" height="6" fill="#5a3a1a"/>
      <rect x="-7" y="2" width="14" height="6" fill={M1.geoGrassDk}/>
      <rect x="-5" y="-4" width="10" height="6" fill={M1.geoHill}/>
      <rect x="-3" y="-10" width="6" height="6" fill={M1.geoGrass}/>
    </g>
  )
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <rect x="-1" y="5" width="2" height="4" fill="#5a3a1a"/>
      <rect x="-8" y="-4" width="16" height="10" fill={M1.geoHill}/>
      <rect x="-5" y="-2" width="5" height="4" fill={M1.geoGrass} opacity=".85"/>
    </g>
  )
}

// ─── Mountain ────────────────────────────────────────────────────────────────
function Mtn({ x, y, w = 60 }: { x: number; y: number; w?: number }) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <polygon points={`${-w / 2},0 ${w / 2},0 0,${-w * 0.85}`} fill={M1.geoMtn}/>
      <polygon points={`0,0 ${w / 2},0 0,${-w * 0.85}`} fill={M1.geoMtnDk}/>
      <polygon points={`${-w * 0.18},${-w * 0.55} ${w * 0.18},${-w * 0.55} 0,${-w * 0.85}`} fill={M1.geoSnow}/>
    </g>
  )
}

// ─── Palm ────────────────────────────────────────────────────────────────────
function Palm({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <rect x="-1" y="-2" width="2" height="14" fill="#7a4a1f"/>
      <rect x="-1" y="3" width="2" height="1" fill="#5a3a1a"/>
      <rect x="-1" y="7" width="2" height="1" fill="#5a3a1a"/>
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values="-3 0 -2; 3 0 -2; -3 0 -2" dur="3s" repeatCount="indefinite"/>
        <polygon points="0,-4 12,-2 8,0" fill={M1.geoGrassDk}/>
        <polygon points="0,-4 -12,-2 -8,0" fill={M1.geoGrassDk}/>
        <polygon points="0,-4 6,-12 2,-6" fill={M1.geoHill}/>
        <polygon points="0,-4 -6,-12 -2,-6" fill={M1.geoHill}/>
      </g>
    </g>
  )
}

// ─── Pyramid ─────────────────────────────────────────────────────────────────
function PyramidM({ x, y, w = 56 }: { x: number; y: number; w?: number }) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <polygon points={`${-w / 2},0 ${w / 2},0 0,${-w * 0.85}`} fill={M1.hisStone}/>
      <polygon points={`0,0 ${w / 2},0 0,${-w * 0.85}`} fill={M1.hisStoneDk}/>
      {[0.2, 0.4, 0.6, 0.8].map((t, i) => (
        <line key={i} x1={-w / 2 * (1 - t)} y1={-w * 0.85 * t} x2={w / 2 * (1 - t)} y2={-w * 0.85 * t}
          stroke={M1.hisShadow} strokeWidth=".6" opacity=".55"/>
      ))}
      <ellipse cx="0" cy={-w * 0.6} rx="4" ry="3" fill={M1.ecoGold}/>
      <ellipse cx="1" cy={-w * 0.6} rx="1.5" ry="2" fill="#3a2613"/>
    </g>
  )
}

// ─── Palace Column (decorative streetside column) ────────────────────────────
function PalaceCol({ x, y, color = '#c8c0b4', banner }: {
  x: number; y: number; color?: string; banner?: boolean
}) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <rect x="-5" y="-20" width="10" height="3" fill={color}/>
      <rect x="-4" y="-17" width="8" height="2" fill={color}/>
      <rect x="-3" y="-15" width="6" height="15" fill="#ece6dc"/>
      <rect x="-3" y="-15" width="1" height="15" fill="#fff" opacity=".65"/>
      <rect x="1"  y="-15" width="1" height="15" fill="#b8aea0" opacity=".45"/>
      <rect x="-4" y="0"   width="8" height="2"  fill={color}/>
      <rect x="-5" y="2"   width="10" height="2" fill={color}/>
      {banner && (
        <g>
          <rect x="3" y="-14" width="8" height="11" fill="#2f6b32"/>
          <rect x="3" y="-14" width="8" height="2" fill="#e8c84a"/>
          <circle cx="7" cy="-7" r="1.6" fill="#e8c84a"/>
        </g>
      )}
    </g>
  )
}

// ─── Civics Capitol Building ──────────────────────────────────────────────────
// Neoclassical government building: dome, columns, steps, arched windows, brickwork.
// Used as the boss node for the Civics region (replaces generic Castle).
function CivicsCapitol({ x, y, flagColor = M1.civAccent, status = 'available', code, name }: {
  x: number; y: number; flagColor?: string; status?: string; code?: string; name?: string
}) {
  const marble  = status === 'locked' ? '#a8a4a0' : '#f0ecea'
  const marbleDk= status === 'locked' ? '#787470' : '#d0c8c0'
  const marbleSh= status === 'locked' ? '#5a5650' : '#b4a898'
  const accent  = status === 'locked' ? '#887880' : M1.civAccent
  const accentDk= status === 'locked' ? '#5a5258' : M1.civAccentDk
  const domeClr = status === 'locked' ? '#909090' : '#e8c84a'
  const domeDk  = status === 'locked' ? '#686868' : '#3a6fb0'

  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      {/* Glow aura */}
      <circle r="52" fill="#ffd55a" opacity=".14">
        <animate attributeName="r" values="48;56;48" dur="3s" repeatCount="indefinite"/>
        <animate attributeName="opacity" values=".14;.28;.14" dur="3s" repeatCount="indefinite"/>
      </circle>

      {/* ── Shadow ── */}
      <ellipse cx="0" cy="30" rx="46" ry="6" fill="#000" opacity=".25"/>

      {/* ── Grand steps (3 tiers) ── */}
      <rect x="-46" y="24" width="92" height="6"  fill={marbleDk}/>
      <rect x="-46" y="24" width="92" height="1"  fill={marble}/>
      <rect x="-40" y="18" width="80" height="6"  fill={marbleDk}/>
      <rect x="-40" y="18" width="80" height="1"  fill={marble}/>
      <rect x="-34" y="12" width="68" height="6"  fill={marbleSh}/>
      <rect x="-34" y="12" width="68" height="1"  fill={marble}/>

      {/* ── Main building body ── */}
      <rect x="-34" y="-8"  width="68" height="20" fill={marble}/>
      {/* Brick coursing (horizontal lines) */}
      {[-4, 0, 4].map(ry => (
        <rect key={ry} x="-34" y={ry} width="68" height="1" fill={marbleDk} opacity=".5"/>
      ))}
      {/* Vertical brick joints (alternating) */}
      {[-22,-10,2,14,26].map((rx, i) => (
        <rect key={i} x={rx} y="-8" width="1" height="10" fill={marbleDk} opacity=".4"/>
      ))}
      {[-28,-16,-4,8,20].map((rx, i) => (
        <rect key={i} x={rx} y="2" width="1" height="10" fill={marbleDk} opacity=".4"/>
      ))}

      {/* ── Arched windows (3) ── */}
      {[-20, 0, 20].map((wx, i) => (
        <g key={i} transform={`translate(${wx},0)`}>
          <rect x="-4" y="-6" width="8" height="10" fill={accentDk}/>
          <path d="M -4 -6 A 4 4 0 0 1 4 -6" fill={accentDk}/>
          <rect x="-3" y="-5" width="6" height="8"  fill="#1a0e2a" opacity=".8"/>
          <path d="M -3 -5 A 3 3 0 0 1 3 -5"  fill="#1a0e2a" opacity=".8"/>
          {/* Window gleam */}
          <rect x="-2" y="-4" width="2" height="4" fill="#fff" opacity=".12"/>
        </g>
      ))}

      {/* ── Entablature (beam above columns) ── */}
      <rect x="-36" y="-10" width="72" height="4"  fill={marbleSh}/>
      <rect x="-36" y="-10" width="72" height="1"  fill={marble}/>
      {/* Triglyphs */}
      {[-28,-14,0,14,28].map((tx, i) => (
        <g key={i}>
          <rect x={tx-3} y="-10" width="6" height="4" fill={accentDk} opacity=".35"/>
          <rect x={tx-2} y="-10" width="1" height="4" fill={accent}   opacity=".25"/>
          <rect x={tx+1} y="-10" width="1" height="4" fill={accent}   opacity=".25"/>
        </g>
      ))}

      {/* ── 8 Columns ── */}
      {[-30,-20,-10,0,10,20,30].map((cx, i) => (
        <g key={i} transform={`translate(${cx},0)`}>
          {/* Shaft */}
          <rect x="-3" y="-8" width="6" height="18" fill={marble}/>
          <rect x="-3" y="-8" width="1" height="18" fill="#fff"    opacity=".5"/>
          <rect x="2"  y="-8" width="1" height="18" fill={marbleSh} opacity=".6"/>
          {/* Capital */}
          <rect x="-4" y="-9"  width="8" height="2" fill={marbleSh}/>
          <rect x="-3" y="-10" width="6" height="1" fill={marble}/>
          {/* Base */}
          <rect x="-4" y="10"  width="8" height="2" fill={marbleSh}/>
        </g>
      ))}

      {/* ── Pediment (triangular tympanum) ── */}
      <polygon points="-36,-10 36,-10 0,-28" fill={marble}/>
      <polygon points="-36,-10 36,-10 0,-28" fill="none" stroke={marbleSh} strokeWidth="1.5"/>
      {/* Pediment relief decoration */}
      <polygon points="-20,-14 20,-14 0,-24" fill={marbleDk} opacity=".3"/>
      <circle cx="0" cy="-19" r="3" fill={accent} opacity=".45"/>

      {/* ── Drum (dome base) ── */}
      <rect x="-14" y="-28" width="28" height="8" fill={marbleDk}/>
      <rect x="-14" y="-28" width="28" height="1" fill={marble}/>
      {/* Drum windows */}
      {[-8,0,8].map((dx,i) => (
        <rect key={i} x={dx-2} y="-26" width="4" height="5" fill={accentDk} opacity=".5"/>
      ))}

      {/* ── Dome ── */}
      <path d="M -14 -20 Q -16 -44 0 -50 Q 16 -44 14 -20 Z" fill={domeClr}/>
      {/* Dome ribs */}
      {[-8,0,8].map((dx,i) => (
        <line key={i} x1={dx} y1="-20" x2={dx * 0.3} y2="-48"
          stroke={domeDk} strokeWidth="1" opacity=".6"/>
      ))}
      {/* Dome highlight */}
      <path d="M -6 -20 Q -8 -44 0 -50 Q 4 -44 2 -20 Z" fill="#fff" opacity=".18"/>
      {/* Dome shadow */}
      <path d="M 6 -20 Q 8 -44 14 -48 L 14 -20 Z" fill="#000" opacity=".12"/>

      {/* ── Lantern (top of dome) ── */}
      <rect x="-4" y="-54" width="8" height="6" fill={domeClr}/>
      <rect x="-5" y="-55" width="10" height="2" fill={domeDk}/>
      <rect x="-3" y="-54" width="1" height="5" fill="#fff" opacity=".4"/>

      {/* ── Flag pole + animated flag ── */}
      <rect x="-0.5" y="-72" width="1" height="18" fill="#3a2613"/>
      <g>
        <animateTransform attributeName="transform" type="scale"
          values="1 1; .55 1; 1 1; -.55 1; 1 1" keyTimes="0;.25;.5;.75;1"
          dur="2s" repeatCount="indefinite" additive="sum"/>
        <polygon points="0,-72 16,-68 16,-62 0,-58" fill={flagColor}/>
        <polygon points="0,-72 16,-68 0,-65"         fill="#fff" opacity=".28"/>
      </g>

      {/* ── Door (centre, arched) ── */}
      <rect x="-5" y="3" width="10" height="9" fill={accentDk}/>
      <path d="M -5 3 A 5 5 0 0 1 5 3" fill={accentDk}/>
      <rect x="-4" y="4" width="8" height="8"  fill="#1a0e2a" opacity=".85"/>
      <path d="M -4 4 A 4 4 0 0 1 4 4" fill="#1a0e2a" opacity=".85"/>
      {/* Door gleam */}
      <rect x="-3" y="5" width="2" height="5" fill="#fff" opacity=".1"/>

      {/* ── Code & name labels ── */}
      {code && <MapLabel y={-80} text={code} fill="#fff"/>}
      {name && <MapLabel y={42} text={name.toUpperCase()} fill="#ffe28a" letterSpacing={0.5}/>}
    </g>
  )
}

// ─── Lighthouse ──────────────────────────────────────────────────────────────
function Lighthouse({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x},${y})`} shapeRendering="crispEdges">
      <polygon points="-6,12 6,12 5,-12 -5,-12" fill="#fff"/>
      <rect x="-5" y="-4" width="10" height="3" fill={M1.ecoBrick}/>
      <rect x="-5" y="4" width="10" height="3" fill={M1.ecoBrick}/>
      <rect x="-4" y="-16" width="8" height="4" fill={M1.ecoGold}/>
      <polygon points="-5,-16 5,-16 0,-22" fill={M1.ecoBrick}/>
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values="0;360" dur="6s" repeatCount="indefinite"/>
        <polygon points="3,-14 28,-22 28,-6" fill={M1.ecoGold} opacity=".35"/>
      </g>
    </g>
  )
}

// ─── Node positions (SVG 1280×800) ───────────────────────────────────────────
// SVG scenery helpers stay for a no-art fallback; the painted map is on by default.
void [Bob, Goat, Hill, PacingCritter, TreeM, Mtn, Palm, PyramidM, PalaceCol, Lighthouse, RealmGate, CivicsCapitol]

const NODE_POS: Record<string, { x: number; y: number; boss: boolean }> = {
  // Geography — mountain grass → meadow → hop the river → Wraith on the east bank.
  // Stops sit on grass only (not pine canopy, not water).
  '6G1.2.2': { x: 172, y: 222, boss: true  },
  '6G1.2.5': { x: 215, y: 200, boss: false },
  '6G1.2.6': { x: 348, y: 198, boss: false },
  '6G2.1.1': { x: 340, y: 250, boss: false },
  '6G2.2.1': { x: 368, y: 348, boss: false },
  '6G3.2.1': { x: 428, y: 346, boss: false },
  '6G4.4.2': { x: 448, y: 308, boss: false },
  '6G5.1.1': { x: 522, y: 248, boss: false },
  '6G5.2.1': { x: 548, y: 228, boss: true  },
  // Civics — processional from the geo-connecting fountain, across the marble, never the market
  '6C1.1.1': { x: 798, y: 312, boss: true  },
  '6C3.6.1': { x: 872, y: 278, boss: false },
  '6C3.6.2': { x: 958, y: 272, boss: false },
  '6C4.3.2': { x: 1058, y: 292, boss: false },
  '6C4.3.3': { x: 1140, y: 322, boss: true  },
  // Economics — market aisles only
  '6E2.3.1': { x: 875,  y: 548, boss: true  },
  '6E3.1.2': { x: 990,  y: 585, boss: false },
  '6E3.3.1': { x: 1115, y: 555, boss: false },
  '6E3.3.3': { x: 1160, y: 648, boss: false },
  '6E3.3.2': { x: 1030, y: 708, boss: true  },
  // History — north sand → around the right pyramid → up the gap trail.
  // Left-pyramid water loop is unused (only if stops won't fit).
  '7W1.2.2':  { x: 148, y: 448, boss: true  },
  '7W1.2.3':  { x: 225, y: 446, boss: false },
  '7W2.1.1':  { x: 280, y: 444, boss: false },
  '7W2.1.3':  { x: 335, y: 444, boss: false },
  '7W2.1.4':  { x: 390, y: 446, boss: false },
  '7W2.1.5':  { x: 445, y: 450, boss: false },
  '7W3.1.1':  { x: 500, y: 458, boss: false },
  '7W3.1.7':  { x: 550, y: 478, boss: false },
  '7W3.1.8':  { x: 605, y: 512, boss: false },
  '7W3.1.10': { x: 645, y: 550, boss: false },
  '7W3.2.1':  { x: 658, y: 598, boss: false },
  '8U3.3.3':  { x: 652, y: 642, boss: false },
  '8U3.3.5':  { x: 635, y: 685, boss: false },
  '8U3.3.6':  { x: 575, y: 684, boss: false },
  '8U3.3.7':  { x: 512, y: 676, boss: false },
  '8U4.1.2':  { x: 462, y: 662, boss: false },
  '8U4.2.2':  { x: 420, y: 650, boss: false },
  '8U4.3.2':  { x: 402, y: 630, boss: false },
  '8U5.2.1':  { x: 392, y: 605, boss: false },
  '8U5.2.2':  { x: 382, y: 582, boss: false },
  '8U5.3.4':  { x: 350, y: 560, boss: true  },
}

// Boss name / FINAL BOSS tag offsets so plates don't stack on START, banners, or neighbors.
const BOSS_LABEL: Record<string, { dx?: number; dy?: number; tagDx?: number; tagDy?: number }> = {
  '6G1.2.2': { dy: 46, tagDx: 58, tagDy: -8 },
  '6G5.2.1': { dx: 12, dy: 54, tagDx: 18, tagDy: -22 },
  '6C1.1.1': { dx: 8, dy: 54, tagDx: 62, tagDy: -8 },
  '6C4.3.3': { dy: 50, tagDx: -8, tagDy: -38 },
  '6E2.3.1': { dy: 44, tagDx: 56, tagDy: -8 },
  '6E3.3.2': { dy: 44, tagDx: -10, tagDy: -36 },
  '7W1.2.2': { dy: 46, tagDx: 58, tagDy: -8 },
  '8U5.3.4': { dx: 78, dy: -52, tagDx: 78, tagDy: -74 },
}

// ─── Path connections ─────────────────────────────────────────────────────────
type PathDef = [string, string] | [string, string, [number, number][]]
const PATHS: PathDef[] = [
  // Geography: mountain grass → meadow → river hop (nodes on banks, line may cross water)
  ['6G1.2.2','6G1.2.5'],
  ['6G1.2.5','6G1.2.6', [[255, 185], [300, 175]]],
  ['6G1.2.6','6G2.1.1'],
  ['6G2.1.1','6G2.2.1', [[348, 300]]],
  ['6G2.2.1','6G3.2.1'],
  ['6G3.2.1','6G4.4.2'],
  ['6G4.4.2','6G5.1.1', [[470, 292], [498, 270]]],
  ['6G5.1.1','6G5.2.1'],
  // Civics: fountain (geo seam) → courtyard → east marble (single processional)
  ['6C1.1.1','6C3.6.1', [[830, 288]]],
  ['6C3.6.1','6C3.6.2'],
  ['6C3.6.2','6C4.3.2', [[1010, 272]]],
  ['6C4.3.2','6C4.3.3'],
  // Economics aisles
  ['6E2.3.1','6E3.1.2'], ['6E3.1.2','6E3.3.1'],
  ['6E3.3.1','6E3.3.3'], ['6E3.3.3','6E3.3.2'],
  // History: north sand → around right pyramid → gap trail (not the left-pyramid water loop)
  ['7W1.2.2','7W1.2.3'], ['7W1.2.3','7W2.1.1'], ['7W2.1.1','7W2.1.3'],
  ['7W2.1.3','7W2.1.4'], ['7W2.1.4','7W2.1.5'], ['7W2.1.5','7W3.1.1'],
  ['7W3.1.1','7W3.1.7'], ['7W3.1.7','7W3.1.8', [[575, 492]]],
  ['7W3.1.8','7W3.1.10'],
  ['7W3.1.10','7W3.2.1'],
  ['7W3.2.1','8U3.3.3'],
  ['8U3.3.3','8U3.3.5', [[648, 668]]],
  ['8U3.3.5','8U3.3.6'], ['8U3.3.6','8U3.3.7'],
  ['8U3.3.7','8U4.1.2'], ['8U4.1.2','8U4.2.2'], ['8U4.2.2','8U4.3.2'],
  ['8U4.3.2','8U5.2.1'], ['8U5.2.1','8U5.2.2'],
  ['8U5.2.2','8U5.3.4', [[365, 568]]],
]

// Final (capstone) boss per realm — last node in path order.
const FINAL_BOSSES = new Set(['6G5.2.1', '6C4.3.3', '6E3.3.2', '8U5.3.4'])

// ─── Biome colours ───────────────────────────────────────────────────────────
// One accent per region — drives QBlock fill, flag colour, paths, and gates.
// Design spec: GEO #3aa83a · CIV #8c4eb8 · ECO #d44a2a · HIS #c08a2a
const BIOME_QBLOCK: Record<string, string> = {
  geography: M1.geoHill,    // #3aa83a
  civics:    M1.civAccent,  // #8c4eb8
  economics: M1.ecoBrick,   // #d44a2a
  history:   M1.hisAccent,  // #c08a2a
}
const BIOME_FLAG: Record<string, string> = {
  geography: M1.geoHill,    // #3aa83a — matches node accent
  civics:    M1.civAccent,  // #8c4eb8
  economics: M1.ecoBrick,   // #d44a2a
  history:   M1.hisAccent,  // #c08a2a
}

// ─── Mobile region nav ────────────────────────────────────────────────────────
// SVG-space focal points for each region (camera snaps here on pill tap)
const REGION_CENTERS_SVG: Record<string, { x: number; y: number }> = {
  geography: { x: 400, y: 270 },
  civics:    { x: 960, y: 300 },
  economics: { x: 1020, y: 620 },
  history:   { x: 390,  y: 600 },
}
const REGION_TABS = [
  { code: 'GEO', biome: 'geography', accent: '#3aa83a' },
  { code: 'CIV', biome: 'civics',    accent: '#8c4eb8' },
  { code: 'ECO', biome: 'economics', accent: '#d44a2a' },
  { code: 'HIS', biome: 'history',   accent: '#c08a2a' },
]

// ─── Main Component ───────────────────────────────────────────────────────────
interface Props {
  standards: Standard[]
  myAttempts: Record<string, string>
}

export default function SuperCivicaWorld({ standards, myAttempts }: Props) {
  const { avatar, textScale, reduceMotion } = useGame()
  TS = textScale  // feed the module-wide label scale (see mts() above)
  const W = 1280, H = 800
  const svgRef   = useRef<SVGSVGElement>(null)
  const wrapRef  = useRef<HTMLDivElement>(null)

  // Extra SVG units that pad the viewBox so the map fills the container without
  // CSS letterbox bars (wide windows get extraX sea; tall windows get extraY).
  const [extraX, setExtraX] = useState(0)
  const [extraY, setExtraY] = useState(0)
  // Container dimensions tracked for mobile pan/zoom
  const [containerW, setContainerW] = useState(1280)
  const containerWRef = useRef(1280)
  const containerHRef = useRef(800)

  // Mobile pan/zoom — use refs for zero-latency updates, mobileTick to trigger render
  const mobilePanXRef       = useRef(0)
  const mobilePanYRef       = useRef(0)
  const mobileZoomRef       = useRef(2.5)
  const [mobileTick, setMobileTick]   = useState(0)
  const mobilePointersRef   = useRef<Map<number, { x: number; y: number }>>(new Map())
  const mobilePinchDistRef  = useRef<number | null>(null)
  const mobilePinchMidRef   = useRef({ x: 0, y: 0 })
  const mobileDragDeltaRef  = useRef(0)
  const isDraggingRef       = useRef(false)
  const mobileInitDoneRef   = useRef(false)
  const [activeRegion, setActiveRegion] = useState('geography')
  const [lockedMsg, setLockedMsg] = useState<string | null>(null)
  const lockedMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setContainerW(width)
      containerWRef.current = width
      containerHRef.current = height
      if (width < 2 || height < 2) return
      const mapAspect = W / H
      const boxAspect = width / height
      if (boxAspect > mapAspect) {
        const svgW = (width / height) * H
        setExtraX(Math.max(0, (svgW - W) / 2))
        setExtraY(0)
      } else {
        const svgH = (height / width) * W
        setExtraX(0)
        setExtraY(Math.max(0, (svgH - H) / 2))
      }
    })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  // Emit scene-ready so GameWorld knows we're up
  useEffect(() => {
    EventBus.emit(EVENTS.SCENE_READY)
  }, [])

  // Reduce motion: pause/resume the SVG's SMIL animations (CSS can't touch SMIL).
  // Honors both the in-app toggle and the OS prefers-reduced-motion setting.
  useEffect(() => {
    const svg = svgRef.current
    if (!svg || typeof svg.pauseAnimations !== 'function') return
    const osReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion || osReduce) {
      svg.setCurrentTime(0)
      svg.pauseAnimations()
    } else {
      svg.unpauseAnimations()
    }
  }, [reduceMotion, standards])

  // ── Mobile pan/zoom helpers ──────────────────────────────────────────────────
  const isMobile = containerW < 640

  const getMobileScale = useCallback((z = mobileZoomRef.current) => {
    return (containerWRef.current / W) * z
  }, [W])

  const clampMobilePan = useCallback((x: number, y: number, z = mobileZoomRef.current) => {
    const s = (containerWRef.current / W) * z
    return {
      x: Math.max(0, Math.min(Math.max(0, W - containerWRef.current / s), x)),
      y: Math.max(0, Math.min(Math.max(0, H - containerHRef.current / s), y)),
    }
  }, [W, H])

  const snapMobileCamera = useCallback((svgX: number, svgY: number, z = mobileZoomRef.current) => {
    const s = (containerWRef.current / W) * z
    const clamped = {
      x: Math.max(0, Math.min(Math.max(0, W - containerWRef.current / s), svgX - containerWRef.current / s / 2)),
      y: Math.max(0, Math.min(Math.max(0, H - containerHRef.current / s), svgY - containerHRef.current / s / 2)),
    }
    mobilePanXRef.current = clamped.x
    mobilePanYRef.current = clamped.y
    mobileZoomRef.current = z
    setMobileTick(t => t + 1)
  }, [W, H])

  const panToRegion = useCallback((biome: string) => {
    const c = REGION_CENTERS_SVG[biome]
    if (c) snapMobileCamera(c.x, c.y)
    setActiveRegion(biome)
  }, [snapMobileCamera])

  // Snap camera to Geography entry on first mobile render (hero position will refine once data loads)
  useEffect(() => {
    if (!isMobile || mobileInitDoneRef.current) return
    mobileInitDoneRef.current = true
    snapMobileCamera(REGION_CENTERS_SVG.geography.x, REGION_CENTERS_SVG.geography.y)
  }, [isMobile, snapMobileCamera])

  // ── Mobile pointer event handlers ────────────────────────────────────────────
  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!isMobile) return
    mobilePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    mobileDragDeltaRef.current = 0
    isDraggingRef.current = false
    if (mobilePointersRef.current.size >= 2) {
      mobilePinchDistRef.current = null
      const vals = Array.from(mobilePointersRef.current.values())
      const mx = (vals[0].x + vals[1].x) / 2
      const my = (vals[0].y + vals[1].y) / 2
      const s = getMobileScale()
      mobilePinchMidRef.current = { x: mobilePanXRef.current + mx / s, y: mobilePanYRef.current + my / s }
    }
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!isMobile) return
    const ptrs = mobilePointersRef.current
    if (!ptrs.has(e.pointerId)) return

    if (ptrs.size === 1) {
      const prev = ptrs.get(e.pointerId)!
      const dx = e.clientX - prev.x
      const dy = e.clientY - prev.y
      mobileDragDeltaRef.current += Math.abs(dx) + Math.abs(dy)
      if (mobileDragDeltaRef.current > 8) isDraggingRef.current = true
      const s = getMobileScale()
      const clamped = clampMobilePan(mobilePanXRef.current - dx / s, mobilePanYRef.current - dy / s)
      mobilePanXRef.current = clamped.x
      mobilePanYRef.current = clamped.y
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY })
      setMobileTick(t => t + 1)
    } else if (ptrs.size >= 2) {
      ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY })
      const vals = Array.from(ptrs.values())
      const p1 = vals[0], p2 = vals[1]
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y)
      const mx = (p1.x + p2.x) / 2
      const my = (p1.y + p2.y) / 2
      if (mobilePinchDistRef.current !== null && dist > 0) {
        const ratio = dist / mobilePinchDistRef.current
        const newZoom = Math.max(0.8, Math.min(5, mobileZoomRef.current * ratio))
        const newS = (containerWRef.current / W) * newZoom
        const clamped = {
          x: Math.max(0, Math.min(Math.max(0, W - containerWRef.current / newS), mobilePinchMidRef.current.x - mx / newS)),
          y: Math.max(0, Math.min(Math.max(0, H - containerHRef.current / newS), mobilePinchMidRef.current.y - my / newS)),
        }
        mobilePanXRef.current = clamped.x
        mobilePanYRef.current = clamped.y
        mobileZoomRef.current = newZoom
        isDraggingRef.current = true
        setMobileTick(t => t + 1)
      }
      mobilePinchDistRef.current = dist
    }
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!isMobile) return
    mobilePointersRef.current.delete(e.pointerId)
    if (mobilePointersRef.current.size < 2) mobilePinchDistRef.current = null
  }

  // Node status computation (mirrors GameWorld.buildNodes logic)
  const nodeStatuses = useMemo(() => {
    const result: Record<string, 'locked' | 'available' | 'attempted' | 'mastered'> = {}
    for (const s of standards) {
      const mastery = myAttempts[s.standard_id]
      const prereqsMet = s.prereq_ids.every(p => {
        const m = myAttempts[p]
        return m && ['proficient', 'mastered'].includes(m)
      })
      result[s.standard_id] =
        mastery === 'mastered'                         ? 'mastered'
        : mastery                                      ? 'attempted'
        : prereqsMet || s.prereq_ids.length === 0     ? 'available'
        : 'locked'
    }
    return result
  }, [standards, myAttempts])

  // Hero position: last attempted/mastered node in sort order; default to geo boss
  const heroPos = useMemo(() => {
    let best: Standard | null = null
    for (const s of standards) {
      const st = nodeStatuses[s.standard_id]
      if (st === 'attempted' || st === 'mastered') {
        if (!best || s.sort_order > best.sort_order) best = s
      }
    }
    const sid = best?.standard_id ?? '6G1.2.2'
    const pos = NODE_POS[sid]
    return pos ? { x: pos.x, y: pos.y - 28 } : { x: 140, y: 222 }
  }, [standards, nodeStatuses])

  // Standards indexed by id for quick lookup
  const stdById = useMemo(() => {
    const m: Record<string, Standard> = {}
    for (const s of standards) m[s.standard_id] = s
    return m
  }, [standards])

  // Realm-level progress, grouped by biome. `done` counts proficient-or-mastered
  // (matching GameWorld's realm-cleared rule); `complete` = every node done.
  // Pure derivation from existing data — no data-model change.
  const realmProgress = useMemo(() => {
    const r: Record<string, { total: number; done: number; mastered: number; complete: boolean }> = {}
    for (const s of standards) {
      const b = s.biome ?? 'geography'
      const cur = r[b] ?? { total: 0, done: 0, mastered: 0, complete: false }
      cur.total++
      const m = myAttempts[s.standard_id]
      if (m === 'mastered') cur.mastered++
      if (m === 'mastered' || m === 'proficient') cur.done++
      r[b] = cur
    }
    for (const b in r) r[b].complete = r[b].total > 0 && r[b].done === r[b].total
    return r
  }, [standards, myAttempts])

  // First available (unlocked, uncleared) standard — used for NEXT BATTLE CTA + "next" state
  const nextStd = useMemo(() => {
    return standards.find(s => nodeStatuses[s.standard_id] === 'available')
  }, [standards, nodeStatuses])

  // Map internal node status → design state enum for visual rendering
  function getDesignState(sid: string): 'locked' | 'available' | 'next' | 'cleared' {
    if (isDone(sid)) return 'cleared'
    const st = nodeStatuses[sid]
    if (st === 'locked') return 'locked'
    // available or attempted-but-not-cleared: mark exactly one as 'next'
    if (sid === nextStd?.standard_id) return 'next'
    return 'available'
  }

  // Click handler — suppressed if a pan/drag just occurred (mobile)
  function handleNodeClick(standardId: string) {
    if (isDraggingRef.current) return
    // Progression gate: locked bosses can't be battled (server enforces this too)
    if (nodeStatuses[standardId] === 'locked') {
      const std = stdById[standardId]
      const firstUnmet = std?.prereq_ids.find(
        p => !['proficient', 'mastered'].includes(myAttempts[p] ?? '')
      )
      const prereqName = (firstUnmet && stdById[firstUnmet]?.boss_name) || 'the previous boss'
      setLockedMsg(`🔒 Defeat ${prereqName} first!`)
      if (lockedMsgTimerRef.current) clearTimeout(lockedMsgTimerRef.current)
      lockedMsgTimerRef.current = setTimeout(() => setLockedMsg(null), 3000)
      return
    }
    EventBus.emit(EVENTS.BOSS_NODE_CLICK, { standardId })
  }

  // Node colour helpers
  function qColor(sid: string): string {
    const st = nodeStatuses[sid]
    if (st === 'mastered') return M1.ecoGold
    if (st === 'locked')   return '#5a5a6a'
    const biome = stdById[sid]?.biome ?? 'geography'
    return BIOME_QBLOCK[biome] ?? '#888'
  }
  function flagColor(sid: string): string {
    const st = nodeStatuses[sid]
    if (st === 'mastered') return M1.ecoGold
    if (st === 'locked')   return '#5a5a6a'
    const biome = stdById[sid]?.biome ?? 'geography'
    return BIOME_FLAG[biome] ?? '#e02828'
  }
  function isLocked(sid: string) { return nodeStatuses[sid] === 'locked' }
  // Proficient-or-mastered — used to "light up" completed path links.
  function isDone(sid: string) {
    const m = myAttempts[sid]
    return m === 'mastered' || m === 'proficient'
  }

  // Avatar → hero colours (hair on top, outfit on body)
  const heroSkin     = hexN(avatar.skin)
  const heroHat      = hexN(avatar.hair)    // hair colour → top of head
  const heroOveralls = hexN(avatar.outfit)  // outfit colour → body

  // Text scale for labels
  const ts = textScale

  // Mobile SVG transform: CSS-space transform that pans/zooms the fixed-size SVG
  const mobileS  = getMobileScale()
  const mobilePX = mobilePanXRef.current
  const mobilePY = mobilePanYRef.current
  // Accent colour for the NEXT BATTLE CTA button
  const nextStdAccent = REGION_TABS.find(r => r.biome === nextStd?.biome)?.accent ?? '#3aa83a'
  // Suppress mobileTick lint warning — it exists solely to trigger re-renders
  void mobileTick

  return (
    <div ref={wrapRef}
      style={{
        width: '100%', height: '100%', overflow: 'hidden', position: 'relative',
        background: M1.seaBleed,
        touchAction: isMobile ? 'none' : 'auto',
      }}
      onPointerDown={isMobile ? handlePointerDown : undefined}
      onPointerMove={isMobile ? handlePointerMove : undefined}
      onPointerUp={isMobile ? handlePointerUp : undefined}
      onPointerCancel={isMobile ? handlePointerUp : undefined}
    >
      <svg ref={svgRef}
        width={isMobile ? W : '100%'}
        height={isMobile ? H : '100%'}
        viewBox={isMobile ? `0 0 ${W} ${H}` : `${-extraX} ${-extraY} ${W + 2 * extraX} ${H + 2 * extraY}`}
        preserveAspectRatio={isMobile ? undefined : 'xMidYMid meet'}
        style={{
          display: 'block', fontFamily: '"Press Start 2P", monospace',
          ...(isMobile ? {
            position: 'absolute', left: 0, top: 0,
            transformOrigin: '0 0',
            transform: `translate(${-mobilePX * mobileS}px, ${-mobilePY * mobileS}px) scale(${mobileS})`,
          } : {}),
        }}>
        <defs>
          {/* userSpaceOnUse: gradient always runs from top of extended viewBox to horizon */}
          <linearGradient id="scw-sky" x1="0" y1={-extraY} x2="0" y2="160"
                          gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={M1.skyHi}/>
            <stop offset="100%" stopColor={M1.sky}/>
          </linearGradient>
          <pattern id="scw-grass" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill={M1.geoGrass}/>
            <rect x="2" y="3" width="2" height="1" fill={M1.geoGrassDk}/>
            <rect x="9" y="9" width="2" height="1" fill={M1.geoGrassDk}/>
          </pattern>
          <pattern id="scw-plaza" width="28" height="28" patternUnits="userSpaceOnUse">
            {/* Limestone pavers — Civics intro ground, not a purple checker */}
            <rect width="28" height="28" fill={M1.civPlazaDk}/>
            <rect x="1" y="1" width="26" height="26" fill={M1.civPlaza}/>
            <rect x="2" y="2" width="24" height="3" fill={M1.civPlazaLt} opacity=".7"/>
            <rect x="2" y="2" width="3" height="24" fill={M1.civPlazaLt} opacity=".35"/>
          </pattern>
          <pattern id="scw-brick" width="20" height="10" patternUnits="userSpaceOnUse">
            <rect width="20" height="10" fill={M1.ecoFloor}/>
            <line x1="0" y1="5" x2="20" y2="5" stroke={M1.ecoFloorDk} strokeWidth=".5"/>
            <line x1="10" y1="0" x2="10" y2="5" stroke={M1.ecoFloorDk} strokeWidth=".5"/>
            <line x1="0" y1="5" x2="0" y2="10" stroke={M1.ecoFloorDk} strokeWidth=".5"/>
          </pattern>
          <pattern id="scw-sand" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill={M1.hisSand}/>
            <circle cx="4" cy="5" r=".8" fill={M1.hisSandDk} opacity=".5"/>
            <circle cx="10" cy="10" r=".8" fill={M1.hisSandDk} opacity=".5"/>
          </pattern>
          <pattern id="scw-sea" width="40" height="20" patternUnits="userSpaceOnUse">
            <rect width="40" height="20" fill={M1.sea}/>
            <path d="M 0 10 q 5 -3 10 0 t 10 0 t 10 0 t 10 0" fill="none" stroke={M1.seaShallow} strokeWidth="1"/>
          </pattern>
          <radialGradient id="scw-sun" cx="86%" cy="14%" r="30%">
            <stop offset="0%" stopColor="#fffae0" stopOpacity=".8"/>
            <stop offset="100%" stopColor="#ffd55a" stopOpacity="0"/>
          </radialGradient>
        </defs>

        {/* Ocean fills every letterbox so resize never shows a sky-colored band. */}
        <rect x={-extraX} y={-extraY} width={W + 2 * extraX} height={H + 2 * extraY} fill={M1.seaBleed}/>
        <image href={overworldArt} x={-480} y={-180} width={2240} height={1160}
          preserveAspectRatio="xMidYMid slice"/>

        {/* ── Sunglasses sun + happy voxel clouds (intro sky) ── */}
        <g transform="translate(1160,78)">
          <g><animateTransform attributeName="transform" type="rotate"
            values="0;360" dur="30s" repeatCount="indefinite"/>
            {[0,45,90,135,180,225,270,315].map(a => (
              <polygon key={a} transform={`rotate(${a})`} points="0,-42 4,-32 -4,-32" fill="#ffe066" opacity=".85"/>
            ))}
          </g>
          <circle r="26" fill="#ffd840"/>
          <circle cx="-5" cy="-3" r="2" fill="#3a2613"/>
          <circle cx="5" cy="-3" r="2" fill="#3a2613"/>
          <rect x="-14" y="-8" width="11" height="6" rx="1" fill="#1a1a12"/>
          <rect x="3" y="-8" width="11" height="6" rx="1" fill="#1a1a12"/>
          <rect x="-4" y="-6" width="8" height="2" fill="#1a1a12"/>
          <rect x="-12" y="-7" width="4" height="2" fill="#7adb7a" opacity=".35"/>
          <rect x="5" y="-7" width="4" height="2" fill="#7adb7a" opacity=".35"/>
          <path d="M -7 6 q 7 5, 14 0" fill="none" stroke="#3a2613" strokeWidth="1.5"/>
        </g>
        <VoxelCloud x={170}  y={70}  scale={1.35} drift={36}/>
        <VoxelCloud x={430}  y={58}  scale={1.05} drift={-22}/>
        <VoxelCloud x={700}  y={62}  scale={1.25} drift={28}/>
        <VoxelCloud x={1225} y={68}  scale={0.95} drift={-18}/>

        {/* ── Realm entry START indicators (one per realm entry boss) ── */}

        {/* Geography entry: 6G1.2.2 — left of the golem so it doesn't cover the name */}
        <g transform="translate(108,188)">
          <g>
            <animateTransform attributeName="transform" type="translate"
              values="0 0; 0 6; 0 0" dur="1s" repeatCount="indefinite"/>
            <polygon points="0,0 14,0 7,16" fill={M1.geoGrassDk} opacity=".9"/>
            <polygon points="2,0 12,0 7,10" fill={M1.geoGrass} opacity=".7"/>
          </g>
          <text textAnchor="middle" x="7" y="-4"
            style={{ font: `${mts(6.5)}px "Press Start 2P", monospace`, fill: M1.geoGrassDk,
                     filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.5))' }}>START</text>
        </g>

        {/* Civics entry: 6C1.1.1 — left of the courtyard marker */}
        <g transform="translate(778,258)">
          <g>
            <animateTransform attributeName="transform" type="translate"
              values="0 0; 0 6; 0 0" dur="1s" begin="0.25s" repeatCount="indefinite"/>
            <polygon points="0,0 14,0 7,16" fill={M1.civAccentDk} opacity=".9"/>
            <polygon points="2,0 12,0 7,10" fill={M1.civAccent} opacity=".7"/>
          </g>
          <text textAnchor="middle" x="7" y="-4"
            style={{ font: `${mts(6.5)}px "Press Start 2P", monospace`, fill: M1.civAccentDk,
                     filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.5))' }}>START</text>
        </g>

        {/* Economics entry: 6E2.3.1 — above the stall, not on the name */}
        <g transform="translate(820,500)">
          <g>
            <animateTransform attributeName="transform" type="translate"
              values="0 0; 0 6; 0 0" dur="1s" begin="0.5s" repeatCount="indefinite"/>
            <polygon points="0,0 14,0 7,16" fill={M1.ecoBrickDk} opacity=".9"/>
            <polygon points="2,0 12,0 7,10" fill={M1.ecoGold} opacity=".85"/>
          </g>
          <text textAnchor="middle" x="7" y="-4"
            style={{ font: `${mts(6.5)}px "Press Start 2P", monospace`, fill: M1.ecoBrickDk,
                     filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.5))' }}>START</text>
        </g>

        {/* History entry: 7W1.2.2 — left of the banner / name stack */}
        <g transform="translate(72,414)">
          <g>
            <animateTransform attributeName="transform" type="translate"
              values="0 0; 0 6; 0 0" dur="1s" begin="0.75s" repeatCount="indefinite"/>
            <polygon points="0,0 14,0 7,16" fill={M1.hisAccent} opacity=".85"/>
            <polygon points="2,0 12,0 7,10" fill={M1.beach} opacity=".6"/>
          </g>
          <text textAnchor="middle" x="7" y="-4"
            style={{ font: `${mts(6.5)}px "Press Start 2P", monospace`, fill: M1.hisAccent,
                     filter: 'drop-shadow(0 1px 0 rgba(0,0,0,.5))' }}>START</text>
        </g>

        {/* ── Dotted paths between nodes ── */}
        {PATHS.map((seg, i) => {
          const [a, b] = seg
          const via = seg.length > 2 ? seg[2] : undefined
          const pa = NODE_POS[a], pb = NODE_POS[b]
          if (!pa || !pb) return null
          const locked = isLocked(b)
          const done = isDone(a) && isDone(b)
          return (
            <DottedPath key={i}
              from={[pa.x, pa.y]} to={[pb.x, pb.y]} via={via}
              color="#fff" locked={locked} done={done}/>
          )
        })}

        {/* ── Coins on paths (decorative) ── */}
        <Coin x={230} y={210}/><Coin x={360} y={320} delay={.2}/><Coin x={500} y={260} delay={.4}/>
        <Coin x={830} y={295} delay={.3}/><Coin x={960} y={288} delay={.5}/>
        <Coin x={250} y={446} delay={.2}/><Coin x={390} y={446} delay={.4}/><Coin x={530} y={470} delay={.6}/>
        <Coin x={650} y={600} delay={.5}/><Coin x={430} y={642} delay={.7}/><Coin x={990} y={620} delay={.8}/>

        {/* ── Boss landmarks & sub-boss nodes ── */}
        {standards.map(std => {
          const pos = NODE_POS[std.standard_id]
          if (!pos) return null
          const ds = getDesignState(std.standard_id)   // 'locked'|'available'|'next'|'cleared'
          const clickable = ds !== 'locked'
          const biome = std.biome ?? 'geography'

          if (pos.boss) {
            return (
              <g key={std.standard_id}
                 onClick={() => handleNodeClick(std.standard_id)}
                 style={{ cursor: clickable ? 'pointer' : 'not-allowed' }}>
                <title>{std.boss_name}{ds === 'locked' ? ' (LOCKED)' : ''}</title>
                <LandmarkBoss
                  x={pos.x} y={pos.y}
                  biome={biome}
                  final={FINAL_BOSSES.has(std.standard_id)}
                  state={ds}
                  flagColor={flagColor(std.standard_id)}
                  name={std.boss_name}
                  labelDx={BOSS_LABEL[std.standard_id]?.dx ?? 0}
                  labelDy={BOSS_LABEL[std.standard_id]?.dy ?? 38}
                />
                {ds === 'cleared' && <MapStar x={pos.x + 28} y={pos.y - 28} r={5}/>}
                {FINAL_BOSSES.has(std.standard_id) && (
                  <FinalBossTag
                    x={pos.x + (BOSS_LABEL[std.standard_id]?.tagDx ?? 54)}
                    y={pos.y + (BOSS_LABEL[std.standard_id]?.tagDy ?? -14)}
                    ts={ts} cleared={ds === 'cleared'}/>
                )}
              </g>
            )
          } else {
            return (
              <g key={std.standard_id}
                 onClick={() => handleNodeClick(std.standard_id)}
                 style={{ cursor: clickable ? 'pointer' : 'not-allowed' }}>
                <title>{std.boss_name}{ds === 'locked' ? ' (LOCKED)' : ''}</title>
                <StateBlock
                  x={pos.x} y={pos.y}
                  state={ds}
                  color={qColor(std.standard_id)}
                  biome={biome}
                  name={std.boss_name}
                />
              </g>
            )
          }
        })}

        {/* ── History finish-line rays (only after the realm is cleared) ── */}
        <g transform="translate(350,560)" opacity={realmProgress['history']?.complete ? 0.5 : 0.18}>
          {realmProgress['history']?.complete && (
            <animateTransform attributeName="transform" type="rotate"
              from="0" to="360" dur="14s" repeatCount="indefinite" additive="sum"/>
          )}
          {[0,30,60,90,120,150,180,210,240,270,300,330].map((deg, i) => (
            <line key={i}
              x1="0" y1="0"
              x2={Math.cos(deg * Math.PI / 180) * 62}
              y2={Math.sin(deg * Math.PI / 180) * 62}
              stroke={realmProgress['history']?.complete ? M1.ecoGold : M1.hisAccent}
              strokeWidth="3" strokeLinecap="round"/>
          ))}
        </g>

        {/* ── Region banners with progress meters ── */}
        <RealmBanner x={360} y={148} w={236} title="GEOGRAPHY FRONTIER"
          dark={M1.geoGrassDk} light={M1.geoHill} progress={realmProgress['geography']} ts={ts}/>
        <RealmBanner x={1165} y={210} w={190} title="CIVICS CAPITOL"
          dark={M1.civAccentDk} light={M1.civAccent} progress={realmProgress['civics']} ts={ts}/>
        <RealmBanner x={1188} y={478} w={172} title="ECO EXCHANGE"
          dark={M1.ecoBrickDk} light={M1.ecoBrick} progress={realmProgress['economics']} ts={ts}/>
        <RealmBanner x={118} y={348} w={176} title="HISTORY REALM"
          dark={M1.hisShadow} light={M1.hisAccent} progress={realmProgress['history']} ts={ts}/>

        {/* Debate Djinn — perched on the capitol dome */}
        <g transform="translate(968,96) scale(1.15)" shapeRendering="crispEdges">
          <ellipse cx="0" cy="16" rx="8" ry="2" fill="#000" opacity=".18"/>
          <ellipse cx="0" cy="4" rx="12" ry="14" fill="#8ee8ff" opacity=".28">
            <animate attributeName="opacity" values=".18;.4;.18" dur="2.2s" repeatCount="indefinite"/>
          </ellipse>
          <rect x="-6" y="-1" width="12" height="15" fill="#e8fbff"/>
          <rect x="-8" y="1" width="3" height="11" fill="#c8f4ff"/>
          <rect x="5" y="1" width="3" height="11" fill="#c8f4ff"/>
          <rect x="-5" y="-12" width="10" height="10" fill="#dff6ff"/>
          <ellipse cx="0" cy="-14" rx="6" ry="4" fill="#f7fdff"/>
          <rect x="-3" y="-8" width="2" height="2" fill="#3ad4ff"/>
          <rect x="1" y="-8" width="2" height="2" fill="#3ad4ff"/>
        </g>

        {/* ── Player at current node ── */}
        <BackpackKid x={heroPos.x} y={heroPos.y} hat={heroHat} overalls={heroOveralls} skin={heroSkin} label="YOU"/>

        {/* ── Loading state: no nodes yet ── */}
        {standards.length === 0 && (
          <g transform="translate(640,387)">
            <MapLabel y={0} text="LOADING WORLD…" fill="#a78bfa" fontSize={11}/>
          </g>
        )}
      </svg>

      {/* ── Mobile bottom dock: NEXT BATTLE CTA + region switcher ── */}
      {isMobile && (
        <div
          onPointerDown={e => e.stopPropagation()}
          onPointerMove={e => e.stopPropagation()}
          onPointerUp={e => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: `8px 10px max(8px, env(safe-area-inset-bottom, 8px))`,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          {/* NEXT BATTLE — big tap target leading to first available node */}
          {nextStd && (
            <button
              onClick={() => { isDraggingRef.current = false; handleNodeClick(nextStd.standard_id) }}
              style={{
                height: 52, border: 'none', borderRadius: 14, cursor: 'pointer',
                background: `linear-gradient(180deg, ${nextStdAccent} 0%, color-mix(in srgb, ${nextStdAccent} 65%, #000) 100%)`,
                color: '#fff', fontFamily: '"Press Start 2P", monospace',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0 18px', boxShadow: '0 4px 20px rgba(0,0,0,0.45)',
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', gap: 5, textAlign: 'left' }}>
                <span style={{ fontSize: 5, color: 'rgba(255,255,255,0.75)', letterSpacing: '1.5px' }}>NEXT BATTLE</span>
                <span style={{ fontSize: 7 }}>{nextStd.boss_name}</span>
              </span>
              <span style={{ fontSize: 16 }}>▶</span>
            </button>
          )}

          {/* Region switcher — 4 pills, active one highlighted */}
          <div style={{
            display: 'flex', gap: 7,
            background: 'rgba(14,13,34,0.88)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(42,45,85,0.9)', borderRadius: 14, padding: 7,
          }}>
            {REGION_TABS.map(r => (
              <div key={r.biome}
                onClick={() => panToRegion(r.biome)}
                style={{
                  flex: 1, height: 44, borderRadius: 10, cursor: 'pointer',
                  background: activeRegion === r.biome ? r.accent : '#16182e',
                  border: `1px solid ${activeRegion === r.biome ? '#fff' : '#262a4a'}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                  transition: 'background 0.15s, border-color 0.15s',
                }}>
                <span style={{ fontFamily: '"Press Start 2P", monospace', fontSize: 7, color: '#fff' }}>{r.code}</span>
                <span style={{ width: 4, height: 4, borderRadius: 4, background: activeRegion === r.biome ? '#fff' : r.accent }}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Locked-boss toast ── */}
      {lockedMsg && (
        <div style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          bottom: isMobile ? 130 : 44, zIndex: 40,
          background: 'rgba(10,10,26,0.94)', border: '2px solid #f59e0b',
          borderRadius: 10, padding: '10px 18px', pointerEvents: 'none',
          fontFamily: '"Press Start 2P", monospace', fontSize: 8, color: '#fcd34d',
          whiteSpace: 'nowrap', animation: 'pulse 1s ease-in-out infinite',
        }}>
          {lockedMsg}
        </div>
      )}
    </div>
  )
}
